import { useCallback, useRef, useState } from 'react';

import { useSelectionBarVisibility } from '~/components/v2/components/private-selection-bar/hooks/useSelectionBarVisibility';
import { type WorkspaceAgentSpacePhase } from '~/components/v2/components/workspace/workspace-agent-panel/components/entry/workspaceAgentSpaceMotion';
import { useIsWorkspaceAgentPanelAvailable } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelAvailable';
import { useIsWorkspaceAgentPanelOverAssetView } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useIsWorkspaceAgentPanelOverAssetView';
import {
  getIsWorkspaceAgentBarCollapsing,
  getIsWorkspaceAgentBarDrawn,
  getWorkspaceAgentBarFreeze,
} from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentBarFreeze';
import {
  isWorkspaceAgentPanelExpanded,
  useWorkspaceAgentPanelStore,
  type WorkspaceAgentDock,
} from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';
import { useWorkspaceSelectionBarStore } from '~/components/v2/store/features/workspace/useWorkspaceSelectionBarStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';

export type UseWorkspaceAgentSpacePhaseParams = {
  /** The canvas editor brings its own agent chat, so the selection bar stands down while it is open. */
  canvasOpen: boolean;
};

export type UseWorkspaceAgentSpacePhaseResult = {
  /** Where the space is headed. Its chrome may still be animating out behind this. */
  phase: WorkspaceAgentSpacePhase;
  /**
   * Shell is still open but the selection behind it has already gone. The chrome has to outlive its
   * own data for this window, or it blinks out instead of sliding.
   */
  isCollapsing: boolean;
  /**
   * Selection bar shell is on screen or sliding — `phase` is `unified` or the hide freeze is still
   * on. Keeps the shell in the tree with live props through the slide-out so bar content does not
   * unmount early.
   */
  isBarDrawn: boolean;
  /** Releases the hide freeze after the hide slide lands. */
  onShellSettled: () => void;
  /** Hand to the enclosing `AnimatePresence`. */
  onExitComplete: () => void;
};

type GetPhaseParams = {
  assetViewOpen: boolean;
  canvasOpen: boolean;
  canShowIdleFab: boolean;
  dock: WorkspaceAgentDock;
  hasPageOwnedSelectionBar: boolean;
  hasSelection: boolean;
};

const getPhase = ({
  assetViewOpen,
  canvasOpen,
  canShowIdleFab,
  dock,
  hasPageOwnedSelectionBar,
  hasSelection,
}: GetPhaseParams): WorkspaceAgentSpacePhase => {
  // Match WORK-174 Affordance: no minimized chrome behind the asset modal — the header Tasks
  // button is the way back in.
  if (canvasOpen || assetViewOpen) {
    return 'hidden';
  }

  if (hasSelection && !hasPageOwnedSelectionBar) {
    return 'unified';
  }

  if (!canShowIdleFab) {
    return 'hidden';
  }

  return isWorkspaceAgentPanelExpanded({ dock }) ? 'hidden' : 'fab';
};

/**
 * Drives the agent selection bar through show / hide, and the pre-ui2026 idle FAB when the header
 * Start task split is not mounted.
 *
 * On ui2026 there is no idle FAB — the bar slides up when something is selected and slides back
 * down when the selection clears. A render-time freeze keeps the shell mounted through the hide
 * slide; motion (with a duration timeout fallback) is what releases it.
 */
export const useWorkspaceAgentSpacePhase = ({
  canvasOpen,
}: UseWorkspaceAgentSpacePhaseParams): UseWorkspaceAgentSpacePhaseResult => {
  const dock = useWorkspaceAgentPanelStore((state) => state.dock);
  const pageOwnedSelectionBarMountCount = useWorkspaceSelectionBarStore(
    (state) => state.pageOwnedSelectionBarMountCount,
  );
  const { hasSelection } = useSelectionBarVisibility();
  const { assetViewOpen } = useIsWorkspaceAgentPanelOverAssetView();
  const { canShowAgentStartTask } = useIsWorkspaceAgentPanelAvailable();
  const { ui2026 } = useTemporaryFeatureFlags();
  const canShowIdleFab = canShowAgentStartTask && !ui2026;

  const phase = getPhase({
    assetViewOpen,
    canvasOpen,
    canShowIdleFab,
    dock,
    hasPageOwnedSelectionBar: pageOwnedSelectionBarMountCount > 0,
    hasSelection,
  });

  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  // Arm on the same render that first shows the bar. `useState` freeze needed a follow-up commit,
  // and Cypress Cloud can Clear before that commit so `isBarDrawn` dropped and the shell popped.
  const freezeBarRef = useRef(phase === 'unified');
  freezeBarRef.current = getWorkspaceAgentBarFreeze({
    didSettle: false,
    freezeBar: freezeBarRef.current,
    phase,
  });

  const [, setBarFreezeGeneration] = useState(0);

  const settleBarFreeze = useCallback(() => {
    freezeBarRef.current = getWorkspaceAgentBarFreeze({
      didSettle: true,
      freezeBar: freezeBarRef.current,
      phase: phaseRef.current,
    });
    setBarFreezeGeneration((generation) => generation + 1);
  }, []);

  return {
    phase,
    isBarDrawn: getIsWorkspaceAgentBarDrawn({ freezeBar: freezeBarRef.current, phase }),
    isCollapsing: getIsWorkspaceAgentBarCollapsing({ freezeBar: freezeBarRef.current, phase }),
    onShellSettled: settleBarFreeze,
    onExitComplete: settleBarFreeze,
  };
};
